/**
 * P0 world renderer (11 + 15 §2/§5.5): baked arena static layer, OT un-draw
 * region, shadows, decals, depth-sorted athletes (blueprint mannequins with
 * per-player un-draw/re-draw timelines) and discs (+ trails, sparks, ghosts),
 * reticle + bounce preview, shield arcs, screen-edge indicators, flash and
 * shake. Hitstop itself lives in `RenderClock`; this module consumes its frame.
 * All randomness is seeded per event (11 §9). Nothing here touches `sim/`.
 */
import { DISC_FLIGHT_Y, DISC_GROUND_TIMEOUT_TICKS, DISC_RADIUS, GRAVITY, JUMP_LAND_TICKS, JUMP_V0, MOVE_SPEED, PLAYER_HEIGHT, PLAYER_RADIUS, SHIELD_ARC_HALF_DEG, SHIELD_PERFECT_TICKS, SHIELD_RADIUS, TICK_DT, VIRTUAL_H, VIRTUAL_W } from '../../../sim/constants.js'
import { reflect, sweepCircleCircle, sweepCircleSegment } from '../../../sim/collision.js'
import { fullBounds } from '../../../sim/arena.js'
import type { Arena, Bounds, DiscState, DiscStateName, PlayerState, PlayerStateName, SimState, Team, Vec2 } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'
import { cameraFor, project, type CameraParams, type Projected } from '../camera.js'
import type { InterpDisc, InterpPlayer } from '../interp.js'
import { drawGlyph, glyphForPowerUp, type GlyphName } from '../glyphs.js'
import { ArenaBake, type BakeResult } from './arena-bake.js'
import { OpenWorldFloor } from './openworld.js'
import { leadPoint } from './lead.js'
import { FOG_FAR, FOG_NEAR, fog } from './openworld-math.js'
import { lightAzimuthSide } from './arena-math.js'
import { buildAthlete, gaitAngle, type AthleteDrawing, type AthletePose, type Polyline } from './athlete.js'
import { initialClips, resolveClips, type ClipInput, type ClipState } from './clips.js'
import { discFace, discSpin } from './disc.js'
import { beatStyle, type BeatStyle } from './beats.js'
import { AMBER, FX_CHEVRON_MAX, FX_DASH_GHOSTS, FX_DISC_REDRAW_MS, FX_DISC_UNDRAW_MS, FX_REDRAW_FAST_MS, FX_REDRAW_MS, FX_THREAT_TIME_S, FX_TICK_ALPHA_IDLE, FX_UNDRAW_MS, INK, INK_RGB, TEAM_ACCENT } from './constants.js'
import { DecalPool } from './decals.js'
import type { ClockFrame } from './hitstop.js'
import { SparkPool } from './sparks.js'
import { TrailSet } from './trails.js'
import { discRedrawStyle, discUndrawStyle, invulnEndStyle, normalStyle, outlineOnlyStyle, redrawStyle, rescindDurationMs, rescindStyle, undrawStyle, type DiscStyle, type LayerStyle } from './undraw.js'

export { TEAM_ACCENT as TEAM_COLOR }

// ── Frame inputs (same shapes as the old render.ts, plus `stt`/`vx,vz`) ────────
export interface RenderPlayer {
  id: string; team: Team; x: number; y: number; z: number; ax: number; az: number
  st: PlayerStateName; stt: number; sag: number; alive: boolean; inv: boolean; sta: number; staMax: number; held: number; local: boolean; pu: string | null; pus: string[]
  vx?: number; vz?: number
  /** S10 jump: grounded flag and landing-lock ticks left (11 §7 jump clip). */
  g: boolean; lt: number
}
export interface RenderDisc { id: number; team: Team; st: DiscStateName; x: number; y: number; z: number; vx: number; vz: number; outline: boolean; stt?: number }

export function fromPlayerState(p: PlayerState, pos: { x: number; y: number; z: number }, tick: number, local: boolean): RenderPlayer {
  return { id: p.id, team: p.team, x: pos.x, y: pos.y, z: pos.z, ax: p.aim.x, az: p.aim.z, st: p.state, stt: p.stateTicks, sag: p.shieldAgeTicks, alive: p.alive, inv: p.invulnUntilTick > tick, sta: p.stamina, staMax: p.tuning.STAMINA_MAX, held: p.discsHeld.length, local, pu: p.powerUps[0] ?? null, pus: p.powerUps.slice(), vx: p.vel.x, vz: p.vel.z, g: p.grounded, lt: p.landTicks }
}
export function fromInterpPlayer(p: InterpPlayer, tick: number): RenderPlayer {
  return { id: p.id, team: p.team, x: p.x, y: p.y, z: p.z, ax: p.ax, az: p.az, st: p.st, stt: p.stt, sag: p.sag, alive: p.alive, inv: p.inv > tick, sta: p.sta, staMax: 100, held: p.held, local: false, pu: p.pu, pus: p.pus, vx: p.vx, vz: p.vz, g: p.g, lt: p.lt }
}
export function fromDiscState(d: DiscState, outline: boolean): RenderDisc {
  return { id: d.id, team: d.team, st: d.state, x: d.pos.x, y: d.pos.y, z: d.pos.z, vx: d.vel.x, vz: d.vel.z, outline, stt: d.stateTicks }
}
export function fromInterpDisc(d: InterpDisc, outline: boolean): RenderDisc {
  return { id: d.id, team: d.team, st: d.st, x: d.x, y: d.y, z: d.z, vx: d.vx, vz: d.vz, outline }
}

export interface RenderOptions { reducedMotion: boolean; flashReduction: boolean; highContrast: boolean; patternStrength: number; offscreenDiscs: boolean }

export interface WorldFrame {
  state: SimState
  cameraTeam: Team
  spectator: boolean
  localId: string | null
  players: RenderPlayer[]
  discs: RenderDisc[]
  aimHit: Vec2 | null
  aimAngle: number
  localPos: Vec2 | null
  showPreview: boolean
  /** 05 §5 lead marker: the local thrower's disc speed and windup (null = off / not holding). */
  lead: { speed: number; windupS: number } | null
  debug: boolean
  /** Wall clock in ms (caller-supplied; nothing in fx/ reads a clock). */
  now: number
  /** World render time (server time domain) from the RenderClock. */
  renderTime: number
  clock: ClockFrame
  options: RenderOptions
  mode: 'hub' | 'match'
  /** Active rig's camera (spec §2). */
  cam: CameraParams
  /** Open-world fog band override (dev sliders); defaults FOG_NEAR/FOG_FAR. */
  fog?: { near: number; far: number }
  /** Terminal position for the off-screen chevron (spec §3 wayfinding). */
  terminal?: Vec2
  /** ms since the results panel appeared (11 §8 match end: world dims to stroke-only), or null. */
  resultsSinceMs: number | null
}

export interface IngestContext { localId: string | null; hub: boolean; positions: (id: string) => { x: number; z: number } | undefined; discPositions?: (id: number) => { x: number; z: number } | undefined }

/** First wall/pillar contact along `aim` from `from`, plus a 3 m reflected stub (05 §5). */
export function bouncePreview(arena: Arena, from: Vec2, aim: number): { hit: Vec2; stub: Vec2 } | null {
  const dx = Math.cos(aim) * 80, dz = Math.sin(aim) * 80
  let best: { t: number; nx: number; nz: number } | null = null
  for (const w of arena.walls) { const h = sweepCircleSegment(from.x, from.z, dx, dz, DISC_RADIUS, w.ax, w.az, w.bx, w.bz); if (h && h.t > 1e-4 && (!best || h.t < best.t)) best = h }
  for (const p of arena.pillars) { const h = sweepCircleCircle(from.x, from.z, dx, dz, DISC_RADIUS, p.x, p.z, p.r); if (h && h.t > 1e-4 && (!best || h.t < best.t)) best = h }
  if (!best) return null
  const hit = { x: from.x + dx * best.t, z: from.z + dz * best.t }
  const [rx, rz] = reflect(dx, dz, best.nx, best.nz)
  const l = Math.hypot(rx, rz) || 1
  return { hit, stub: { x: hit.x + (rx / l) * 3, z: hit.z + (rz / l) * 3 } }
}

// ── Timelines ───────────────────────────────────────────────────────────────
type Timeline =
  | { kind: 'pendingDerez'; since: number }
  | { kind: 'undraw'; startAt: number }
  | { kind: 'rescind'; startAt: number; abortProgress: number }
  | { kind: 'redraw'; startAt: number; totalMs: number; outlineAfter: boolean }
  | { kind: 'outline' }
  | { kind: 'invulnEnd'; startAt: number }
type DiscMotif = { kind: 'redraw' | 'undraw'; startAt: number }

interface Ghost { id: string; bornAt: number; x: number; z: number; dx: number; dz: number }
interface Flourish { kind: 'catch' | 'pickup' | 'steal' | 'skid'; x: number; z: number; bornAt: number; team: Team; fromTeam: Team; dx?: number; dz?: number }
const FLOURISH_MS: Record<Flourish['kind'], number> = { catch: 80, pickup: 80, steal: 120, skid: 200 }

const ARC_HALF = (SHIELD_ARC_HALF_DEG * Math.PI) / 180
/** Jump apex (m): JUMP_V0² / (2·GRAVITY) = 1.444 (spec S10 §1.1); the tuck and the shadow shrink saturate here. */
const JUMP_APEX_M = (JUMP_V0 * JUMP_V0) / (2 * GRAVITY)

export class WorldRenderer {
  private bake = new ArenaBake()
  private openFloor = new OpenWorldFloor()
  private lastFloorStats = { lines: 0, pillars: 0 }
  readonly sparks = new SparkPool()
  readonly decals = new DecalPool()
  readonly trails = new TrailSet()
  readonly timelines = new Map<string, Timeline>()
  readonly discMotifs = new Map<number, DiscMotif>()
  private ghosts: Ghost[] = []
  private flourishes: Flourish[] = []
  private shudder = new Map<string, number>() // playerId → until (wall ms)
  private lowering = new Map<string, number>() // playerId → lowered at
  private flicker = new Map<string, number>() // exhaust 1-frame flicker
  private shieldHoldSince = new Map<string, number>()
  private lastPos = new Map<string, { x: number; y: number; z: number; t: number; vx: number; vz: number; vy: number }>()
  private nearMiss = new Map<number, number>() // discId → flagged at
  private nearMissSeen = new Set<number>()
  private lastDerezPos = new Map<string, { x: number; z: number; ax: number; az: number; st: PlayerStateName; stt: number }>()
  /** 11 §7 clip layers per player, keyed like the timelines. */
  private clipStates = new Map<string, ClipState>()
  /** Wall time of the last `throw` event per player — reconstructs a windup the snapshots skipped. */
  private throwStarts = new Map<string, number>()
  private debugBakeLayers = false
  /** Pose + drawing per player for the current frame (built once in step 3, reused by step 5). */
  private frameDrawings = new Map<string, { pose: AthletePose; drawing: AthleteDrawing }>()
  /** 11 §8 beat style for the current frame, plus once-per-phase latches for the one-frame beats. */
  private beat: BeatStyle = beatStyle({ mode: 'hub', phase: 'LIVE', phaseMs: 0, timerS: null, winner: null, resultsSinceMs: null, reducedMotion: false })
  private phaseMs = 0
  private beatPhase: string | null = null
  private beatLatch = { otFrame: false, winner: false, discUndraw: false }
  localScreen = { x: VIRTUAL_W / 2, y: VIRTUAL_H * 0.75 }
  camTeam: Team = 0
  private frameNo = 0

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  /** Spec §5 ARENA BAKE: bake both team cameras for the fixed rig so the first frame is a cache hit. */
  prewarmBake(arena: Arena, pixelScale: number): void {
    const cam = cameraFor(arena) // S10: the arena's own lens, so an XL hub's first frame is a cache hit too
    for (const team of [0, 1] as Team[]) { const b = this.bake.get(arena, team, pixelScale, {}, cam); if (b.ms > 0) this.lastBakeMs = b.ms }
  }

  /** Static-layer review flag (15 §2.1): bake each layer separately for `?debug=bake`. */
  setDebugBakeLayers(on: boolean): void { this.debugBakeLayers = on }

  /** Last static-bake duration (ms), for the perf script (15 §2.1 budget). */
  lastBakeMs = 0
  debugInfo(): { sparks: number; decals: number; timelines: number; discMotifs: number; ghosts: number; bakeMs: number; floorLines: number; floorPillars: number } {
    return { sparks: this.sparks.size, decals: this.decals.size, timelines: this.timelines.size, discMotifs: this.discMotifs.size, ghosts: this.ghosts.length, bakeMs: this.lastBakeMs, floorLines: this.lastFloorStats.lines, floorPillars: this.lastFloorStats.pillars }
  }

  // ── Events → FX (called once per snapshot, before draw) ────────────────────
  ingest(events: SimEvent[], now: number, c: IngestContext, reducedMotion = false): void {
    const discIndex = new Map<number, number>()
    for (const e of events) {
      switch (e.type) {
        case 'derez': {
          // T0 is the hitstop release; hold the pose until then (11 §3.2 S0).
          const cur = this.timelines.get(e.playerId)
          if (cur && (cur.kind === 'undraw' || cur.kind === 'pendingDerez')) break
          this.timelines.set(e.playerId, { kind: 'pendingDerez', since: now })
          break
        }
        case 'rescind': {
          const cur = this.timelines.get(e.playerId)
          if (!cur) break
          if (cur.kind === 'pendingDerez') { this.timelines.delete(e.playerId); break }
          if (cur.kind === 'undraw') {
            const prog = Math.min(1, (now - cur.startAt) / FX_UNDRAW_MS)
            this.timelines.set(e.playerId, { kind: 'rescind', startAt: now, abortProgress: prog })
          }
          break
        }
        case 'respawn':
          this.timelines.set(e.playerId, { kind: 'redraw', startAt: now, totalMs: FX_REDRAW_MS, outlineAfter: e.invulnUntilTick > e.tick })
          break
        case 'invulnEnd': {
          const cur = this.timelines.get(e.playerId)
          if (!cur || cur.kind === 'outline' || cur.kind === 'redraw') this.timelines.set(e.playerId, { kind: 'invulnEnd', startAt: now })
          break
        }
        case 'phase':
          if (e.phase === 'RESET') {
            // 11 §8 round reset: fast re-draw, decals + trails cleared, motifs dropped.
            for (const id of [...this.timelines.keys()]) this.timelines.set(id, { kind: 'redraw', startAt: now, totalMs: FX_REDRAW_FAST_MS, outlineAfter: false })
            this.resetRoundIds = true
            this.decals.clear(); this.trails.clear(); this.sparks.clear(); this.discMotifs.clear(); this.ghosts.length = 0; this.nearMissSeen.clear()
          }
          break
        case 'rematerialise':
          this.discMotifs.set(e.discId, { kind: 'redraw', startAt: now })
          break
        case 'despawn': {
          const idx = discIndex.size
          discIndex.set(e.discId, idx)
          // Round-end clears stagger 0–80 ms by disc index.
          this.discMotifs.set(e.discId, { kind: 'undraw', startAt: now + Math.min(80, idx * 20) })
          break
        }
        case 'bounce': {
          if (e.surface === 'shield') break
          const surf = e.surface === 'pillar' ? 'pillar' : 'wall'
          this.sparks.burst(surf, `b${e.tick}:${e.discId}:${e.bounceCount}`, e.x, e.z, e.nx, e.nz, now)
          this.decals.add(e.x, e.z, e.nx, e.nz, now, surf, e.bounceCount)
          if (surf === 'pillar') this.pillarBright = now
          break
        }
        case 'deflectLate': {
          const p = c.positions(e.playerId)
          if (p) this.sparks.burst('shieldLate', `dl${e.tick}:${e.discId}`, p.x, p.z, 1, 0, now)
          this.shudder.set(e.playerId, now + 90)
          break
        }
        case 'deflectPerfect': {
          const p = c.positions(e.playerId)
          if (p) this.sparks.burst('shieldPerfect', `dp${e.tick}:${e.discId}`, p.x, p.z, 1, 0, now, null)
          this.perfectBright.set(e.playerId, now)
          // Trail switches to the new owner's accent instantly (team resolved at draw from the disc).
          break
        }
        case 'clash':
          this.sparks.burst('clash', `c${e.tick}:${e.discA}:${e.discB}`, e.x, e.z, 1, 0, now)
          this.trails.cut(e.discA); this.trails.cut(e.discB)
          this.clashKick = now
          break
        case 'ground':
          if (e.reason === 'drop' || e.reason === 'holderDead' || e.reason === 'slow' || e.reason === 'range' || e.reason === 'bounces') this.sparks.burst('dust', `g${e.tick}:${e.discId}`, e.x, e.z, 0, 1, now)
          if (e.reason === 'slow') {
            // 11 §4 GROUND: the slide leaves a faint 200 ms skid stroke along the last travel direction.
            const smp = this.trails.get(e.discId)?.samples ?? []
            const a = smp[Math.max(0, smp.length - 3)], b = smp[smp.length - 1]
            const dx = a && b ? b.x - a.x : 0, dz = a && b ? b.z - a.z : 1
            const l = Math.hypot(dx, dz) || 1
            this.flourishes.push({ kind: 'skid', x: e.x, z: e.z, bornAt: now, team: 0, fromTeam: 0, dx: dx / l, dz: dz / l })
          }
          break
        case 'catch': {
          const p = c.positions(e.playerId)
          if (p) this.flourishes.push({ kind: 'catch', x: p.x, z: p.z, bornAt: now, team: 0, fromTeam: 0 })
          break
        }
        case 'pickup': {
          const p = c.positions(e.playerId)
          if (p) this.flourishes.push({ kind: e.stolen ? 'steal' : 'pickup', x: p.x, z: p.z, bornAt: now, team: 0, fromTeam: 1 })
          break
        }
        case 'dash': {
          if (reducedMotion) break
          const p = c.positions(e.playerId)
          if (p) this.ghosts.push({ id: e.playerId, bornAt: now, x: p.x, z: p.z, dx: e.dx, dz: e.dz })
          break
        }
        case 'exhaust': this.flicker.set(e.playerId, this.frameNo + 1); break
        case 'throw': this.throwStarts.set(e.playerId, now); break
        case 'shieldLower': this.lowering.set(e.playerId, now); this.shieldHoldSince.delete(e.playerId); break
        case 'shieldRaise': this.shieldHoldSince.set(e.playerId, now); break
        default: break
      }
    }
  }
  private resetRoundIds = false
  private pillarBright = -Infinity
  private clashKick = -Infinity
  private perfectBright = new Map<string, number>()

  /** Wall time of the last clash (main.ts can feed RenderClock.kick). */
  takeClashKick(): number | null { const k = this.clashKick; this.clashKick = -Infinity; return Number.isFinite(k) ? k : null }

  // ── Draw ────────────────────────────────────────────────────────────────────
  draw(f: WorldFrame, pixelScale: number): void {
    const ctx = this.ctx
    const arena = f.state.arena
    const team = f.cameraTeam
    this.camTeam = team
    this.frameNo++
    const now = f.now
    const P = (x: number, y: number, z: number) => project(x, y, z, team, arena, f.cam)
    // 11 §8 beats for this frame (pure), with the one-shot beats latched per phase entry.
    const m = f.state.match
    const phaseMs = m.phaseTicks * TICK_DT * 1000
    const lastRound = m.roundResults[m.roundResults.length - 1]
    const beat = beatStyle({ mode: f.mode, phase: m.phase, phaseMs, timerS: m.phase === 'LIVE' ? m.timerTicks * TICK_DT : null, winner: m.phase === 'ROUND_END' ? lastRound?.winner ?? null : null, resultsSinceMs: f.resultsSinceMs, reducedMotion: f.options.reducedMotion })
    this.beat = beat; this.phaseMs = phaseMs
    if (m.phase !== this.beatPhase) { this.beatPhase = m.phase; this.beatLatch = { otFrame: false, winner: false, discUndraw: false } }
    const otFrame = beat.strokeOnlyFrame && !this.beatLatch.otFrame
    if (beat.strokeOnlyFrame) this.beatLatch.otFrame = true
    if (beat.winnerBright !== null && !this.beatLatch.winner) { this.beatLatch.winner = true; for (const p of f.players) if (p.team === beat.winnerBright) this.perfectBright.set(p.id, now) }
    if (m.phase === 'ROUND_END' && phaseMs >= beat.discsUndrawAtMs && !this.beatLatch.discUndraw) {
      // Round end: loose discs un-draw together, staggered 0–80 ms by index (held discs follow in drawPlayer).
      this.beatLatch.discUndraw = true
      let idx = 0
      for (const d of f.discs) if (d.st !== 'HELD' && !this.discMotifs.has(d.id)) this.discMotifs.set(d.id, { kind: 'undraw', startAt: now + Math.min(80, idx++ * 20) })
    }
    const strokeOnly = ((f.clock.strokeOnlyFrame || otFrame) && !f.options.flashReduction) || beat.worldDim >= 1
    // Spec §3: follow arenas draw the procedural floor with fog + culling; fixed arenas keep the bake.
    const open = arena.camera === 'follow'
    const fogNear = f.fog?.near ?? FOG_NEAR, fogFar = f.fog?.far ?? FOG_FAR
    const fogA = (depth: number): number => (open ? 1 - fog(depth, fogNear, fogFar) : 1)
    const cull = (pr: Projected): boolean => !pr.visible || (open && (pr.depth > fogFar || pr.sx < -200 || pr.sx > VIRTUAL_W + 200))
    const hc = f.options.highContrast

    // Resolve pending derez timelines at the hitstop release (T0).
    for (const [id, tl] of this.timelines) {
      if (tl.kind === 'pendingDerez' && now >= f.clock.releaseAt) this.timelines.set(id, { kind: 'undraw', startAt: Math.max(tl.since, f.clock.releaseAt) })
    }

    ctx.save()
    ctx.translate(f.clock.shake.x, f.clock.shake.y)

    // 1. Floor: static bake (fixed camera) or the open-world procedural floor (follow camera).
    let baked: BakeResult | null = null
    if (open) this.lastFloorStats = this.openFloor.draw(ctx, arena, team, f.cam, { fogNear, fogFar, strokeOnly })
    else {
      baked = this.bake.get(arena, team, pixelScale, { debugLayers: this.debugBakeLayers }, f.cam)
      if (baked.ms > 0) this.lastBakeMs = baked.ms
      ctx.drawImage(baked.full, 0, 0, VIRTUAL_W, VIRTUAL_H)
    }
    {
      // Stroke-only frames (flash, OT start) and the results dim share one paper wash over the world.
      const dim = Math.max(strokeOnly ? 0.88 : 0, 0.88 * beat.worldDim)
      if (dim > 0) { ctx.fillStyle = `rgba(12,14,17,${dim.toFixed(3)})`; ctx.fillRect(-20, -20, VIRTUAL_W + 40, VIRTUAL_H + 40) }
    }
    // Last 10 s: the floor seams nearest each back wall pulse at 1 Hz (neutral ink over the bake).
    if (beat.seamPulse > 0 && baked) {
      const hw = arena.width / 2
      ctx.save(); ctx.strokeStyle = `rgba(${INK_RGB},${(0.25 * beat.seamPulse).toFixed(3)})`; ctx.lineWidth = 1
      for (const z of [2, 4, arena.length - 2, arena.length - 4]) { const a = P(-hw, 0, z), b = P(hw, 0, z); if (a.visible && b.visible) { ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke() } }
      ctx.restore()
    }

    // 2. OT un-draw region: floor outside `bounds[team]` shows the grid-only bake.
    for (const tm of baked ? ([0, 1] as Team[]) : []) {
      const b = f.state.match.bounds[tm], full = fullBounds(arena, tm)
      const shrunk = !(b.xMin === full.xMin && b.xMax === full.xMax && b.zMin === full.zMin && b.zMax === full.zMax)
      if (!shrunk && !f.debug) continue
      if (shrunk) this.drawOtRegion(P, b, full, baked!.gridOnly)
      this.drawBounds(P, b, shrunk ? `rgba(${INK_RGB},0.85)` : 'rgba(120,200,255,0.25)', shrunk, tm)
    }
    if (f.debug) this.drawGizmosStatic(P, arena)

    // 3. Shadows (40 % ink, no blur) — under everything dynamic. The athlete drawing is built here once
    //    per player (its stance sets the shadow ellipse, 15 §2.2) and reused by step 5.
    this.frameDrawings.clear()
    {
      const seen = new Set(f.players.map((p) => p.id))
      for (const id of [...this.clipStates.keys()]) if (!seen.has(id)) { this.clipStates.delete(id); this.throwStarts.delete(id) }
    }
    for (const p of f.players) {
      const pr = P(p.x, p.y, p.z); if (cull(pr)) continue
      const pose = this.poseFor(p, pr, f)
      const drawing = buildAthlete(pose)
      this.frameDrawings.set(p.id, { pose, drawing })
      const style = this.styleFor(p, now, f.options)
      if (style.shadowAlpha <= 1e-6) continue
      const g = P(p.x, 0, p.z); if (!g.visible) continue
      // S10 jump: the shadow stays on the floor and shrinks with height (rx × (1 − 0.35·y/1.44), 11 §7).
      const hk = 1 - 0.35 * Math.min(1, Math.max(0, p.y) / JUMP_APEX_M)
      ctx.fillStyle = `rgba(0,0,0,${0.4 * style.shadowAlpha * fogA(pr.depth)})`
      ctx.beginPath(); ctx.ellipse(g.sx, g.sy, Math.max(drawing.shadow.rx * hk, 2), Math.max(drawing.shadow.ry * hk, 1), 0, 0, Math.PI * 2); ctx.fill()
    }
    for (const d of f.discs) {
      if (d.st === 'HELD') continue
      const pr = P(d.x, 0, d.z); if (cull(pr)) continue
      ctx.fillStyle = `rgba(0,0,0,${0.3 * fogA(pr.depth)})`
      ctx.beginPath(); ctx.ellipse(pr.sx, pr.sy, DISC_RADIUS * pr.scale, DISC_RADIUS * pr.scale * 0.35, 0, 0, Math.PI * 2); ctx.fill()
    }

    // 4. Decals.
    this.decals.prune(now)
    for (const d of this.decals.decals) { const pr = P(d.x, 0, d.z); if (cull(pr)) continue; this.drawDecal(P, d, this.decals.alpha(d, now) * fogA(pr.depth)) }

    // 5. Painter's sort: athletes, discs, trails, sparks, ghosts by depth.
    type Item = { depth: number; draw: () => void }
    const items: Item[] = []
    const local = f.players.find((p) => p.local) ?? null
    for (const p of f.players) {
      const pr = P(p.x, p.y, p.z)
      if (cull(pr)) continue
      if (p.local) this.localScreen = { x: pr.sx, y: pr.sy }
      items.push({ depth: pr.depth, draw: () => this.withFog(fogA(pr.depth), () => this.drawPlayer(pr, p, f, strokeOnly)) })
    }
    this.ghosts = this.ghosts.filter((g) => now - g.bornAt < 90 * FX_DASH_GHOSTS)
    for (const g of this.ghosts) {
      const p = f.players.find((q) => q.id === g.id); if (!p) continue
      for (let i = 1; i <= FX_DASH_GHOSTS; i++) {
        const age = now - g.bornAt - (i - 1) * 30
        if (age < 0 || age > 90) continue
        const back = i * 0.45
        const pr = P(p.x - g.dx * back, p.y, p.z - g.dz * back); if (cull(pr)) continue
        items.push({ depth: pr.depth + 0.01, draw: () => this.withFog(fogA(pr.depth), () => this.drawGhost(pr, p, f, 1 - age / 90)) })
      }
    }
    for (const d of f.discs) {
      const pr = P(d.x, d.y, d.z)
      const tr = this.trails.push(d.id, d.st, d.team, d.x, d.z, f.renderTime)
      if (tr && !cull(pr)) items.push({ depth: pr.depth + 0.005, draw: () => this.withFog(fogA(pr.depth), () => this.drawTrail(P, tr, f.options)) })
      if (cull(pr)) continue
      // HELD discs are layer 10 of the holder's athlete (11 §3.1); drawDisc only
      // handles them while a re-materialise motif is playing.
      if (d.st === 'HELD' && !this.discMotifs.has(d.id)) continue
      items.push({ depth: pr.depth - 0.01, draw: () => this.withFog(fogA(pr.depth), () => this.drawDisc(pr, d, P, f, strokeOnly)) })
    }
    this.sparks.prune(now)
    for (const s of this.sparks.sparks) { const pr = P(s.x, DISC_FLIGHT_Y, s.z); if (cull(pr)) continue; items.push({ depth: pr.depth - 0.02, draw: () => this.withFog(fogA(pr.depth), () => this.drawSpark(P, s, now)) }) }
    this.flourishes = this.flourishes.filter((fl) => now - fl.bornAt < FLOURISH_MS[fl.kind] + 20)
    for (const fl of this.flourishes) { const pr = P(fl.x, fl.kind === 'skid' ? 0 : 1.0, fl.z); if (cull(pr)) continue; items.push({ depth: pr.depth - 0.03, draw: () => this.withFog(fogA(pr.depth), () => fl.kind === 'skid' ? this.drawSkid(P, fl, now) : this.drawFlourish(pr, fl, now)) }) }
    items.sort((a, b) => b.depth - a.depth)
    for (const it of items) it.draw()

    // 6. Reticle + preview (local only; preview hidden during THROW_RECOVERY).
    if (f.localPos && f.aimHit && local && local.alive) {
      const r = P(f.aimHit.x, DISC_FLIGHT_Y, f.aimHit.z)
      if (r.visible) {
        ctx.strokeStyle = `rgba(${INK_RGB},0.85)`; ctx.lineWidth = 1.5
        const s = Math.max(5, 0.35 * r.scale), gap = Math.max(1, 0.1 * r.scale)
        ctx.beginPath(); ctx.moveTo(r.sx - s, r.sy); ctx.lineTo(r.sx - gap, r.sy); ctx.moveTo(r.sx + gap, r.sy); ctx.lineTo(r.sx + s, r.sy)
        ctx.moveTo(r.sx, r.sy - s * 0.5); ctx.lineTo(r.sx, r.sy - gap * 0.5); ctx.moveTo(r.sx, r.sy + gap * 0.5); ctx.lineTo(r.sx, r.sy + s * 0.5); ctx.stroke()
      }
      if (f.showPreview && local.st !== 'THROW_RECOVERY') {
        const pv = bouncePreview(arena, f.localPos, f.aimAngle)
        if (pv) {
          const a = P(f.localPos.x, DISC_FLIGHT_Y, f.localPos.z), h = P(pv.hit.x, DISC_FLIGHT_Y, pv.hit.z), st = P(pv.stub.x, DISC_FLIGHT_Y, pv.stub.z)
          ctx.save(); ctx.setLineDash([6, 4]); ctx.lineWidth = 1
          ctx.strokeStyle = `rgba(${INK_RGB},0.6)`; ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(h.sx, h.sy); ctx.stroke()
          ctx.strokeStyle = `rgba(${INK_RGB},0.4)`; ctx.beginPath(); ctx.moveTo(h.sx, h.sy); ctx.lineTo(st.sx, st.sy); ctx.stroke()
          ctx.restore()
          ctx.fillStyle = `rgba(${INK_RGB},0.85)`; ctx.beginPath(); ctx.arc(h.sx, h.sy, 2.5, 0, Math.PI * 2); ctx.fill()
        }
      }
    }

    // 6b. Lead markers (05 §5): a faint ring where each moving enemy will be when a disc thrown
    // now lands. Information only — the reticle still has to go there. Hidden while not holding.
    if (f.lead && f.localPos && local && local.alive && local.held > 0 && local.st !== 'THROW_RECOVERY') {
      for (const p of f.players) {
        if (p.local || p.team === local.team || !p.alive || p.vx === undefined || p.vz === undefined) continue
        if (Math.hypot(p.vx, p.vz) < 0.5) continue
        const lp = leadPoint(f.localPos, { x: p.x, z: p.z }, { x: p.vx, z: p.vz }, f.lead.speed, f.lead.windupS)
        const pr = P(lp.x, DISC_FLIGHT_Y, lp.z); if (!pr.visible) continue
        const r = Math.max(3, PLAYER_RADIUS * pr.scale)
        ctx.save(); ctx.setLineDash([3, 3]); ctx.lineWidth = 1; ctx.strokeStyle = `rgba(${INK_RGB},0.45)`
        ctx.beginPath(); ctx.ellipse(pr.sx, pr.sy, r, r * 0.45, 0, 0, Math.PI * 2); ctx.stroke(); ctx.restore()
        ctx.fillStyle = `rgba(${INK_RGB},0.6)`; ctx.beginPath(); ctx.arc(pr.sx, pr.sy, 1.5, 0, Math.PI * 2); ctx.fill()
      }
    }

    // 7. Shield arcs (public info, every shielding player).
    for (const p of f.players) {
      const pr = P(p.x, p.y, p.z); if (cull(pr)) continue
      this.withFog(fogA(pr.depth), () => this.drawShieldArc(pr, p, now, f))
    }

    ctx.restore() // end shake

    // 8. Screen-edge indicators (own team's view; spectators get both at lower alpha).
    if (f.options.offscreenDiscs) this.drawEdgeIndicators(P, f, local)

    // 9. Flash: multiply the eliminated team's accent (never white/red).
    if (f.clock.flash && !f.options.flashReduction) {
      const fl = f.clock.flash
      const team = fl.team === 'alt' ? ((fl.frameIndex % 2) as Team) : fl.team
      ctx.save(); ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = 1
      ctx.fillStyle = TEAM_ACCENT[team]
      ctx.globalAlpha = 1
      // Multiply at FX_FLASH_ALPHA: blend accent over white-ish then multiply.
      ctx.fillStyle = mixToWhite(TEAM_ACCENT[team], 1 - 0.18)
      ctx.fillRect(0, 0, VIRTUAL_W, VIRTUAL_H)
      ctx.restore()
    }
    if (hc) void 0
  }

  // ── Athletes ───────────────────────────────────────────────────────────────
  private styleFor(p: RenderPlayer, now: number, o: RenderOptions): LayerStyle {
    const tl = this.timelines.get(p.id)
    if (tl) {
      switch (tl.kind) {
        case 'pendingDerez': return normalStyle()
        case 'undraw': { const s = undrawStyle(now - tl.startAt, o.reducedMotion); if (s.done) { this.timelines.delete(p.id); return s } return s }
        case 'rescind': { const s = rescindStyle(tl.abortProgress, now - tl.startAt); if (now - tl.startAt >= rescindDurationMs(tl.abortProgress)) this.timelines.delete(p.id); return s }
        case 'redraw': {
          const s = redrawStyle(now - tl.startAt, tl.totalMs)
          if (s.done || now - tl.startAt >= tl.totalMs) { if (tl.outlineAfter && p.inv) { this.timelines.set(p.id, { kind: 'outline' }); return outlineOnlyStyle() } this.timelines.delete(p.id); return normalStyle() }
          if (tl.outlineAfter) { for (const l of s.layers) l.fillFrac = 0 }
          return s
        }
        case 'outline': { if (!p.inv) { this.timelines.set(p.id, { kind: 'invulnEnd', startAt: now }); return invulnEndStyle(0) } return outlineOnlyStyle() }
        case 'invulnEnd': { const s = invulnEndStyle(now - tl.startAt); if (s.done) this.timelines.delete(p.id); return s }
      }
    }
    if (!p.alive) {
      // Dead with no timeline (joined mid-derez, or un-draw finished): invisible.
      const s = normalStyle(); for (const l of s.layers) { l.alpha = 0; l.fillFrac = 0 } s.heightTickAlpha = 0; s.shadowAlpha = 0; return s
    }
    if (p.inv) return outlineOnlyStyle()
    return normalStyle()
  }

  private poseFor(p: RenderPlayer, pr: Projected, f: WorldFrame): AthletePose {
    const cam = f.cameraTeam
    const aimS = cam === 0 ? { x: p.ax, y: p.az } : { x: -p.ax, y: -p.az } // y = toward the far wall
    // Velocity: from the snapshot when present, else from frame deltas.
    let vx = p.vx, vz = p.vz
    const lp = this.lastPos.get(p.id)
    if (vx === undefined || vz === undefined) {
      if (lp && f.renderTime > lp.t) { const dt = (f.renderTime - lp.t) / 1000; vx = (p.x - lp.x) / dt; vz = (p.z - lp.z) / dt; vx = lp.vx * 0.5 + vx * 0.5; vz = lp.vz * 0.5 + vz * 0.5 } else { vx = lp?.vx ?? 0; vz = lp?.vz ?? 0 }
    }
    // Vertical velocity estimate from the y delta (rise vs fall for the jump clip); a frozen clock keeps the last sign.
    let vy = lp?.vy ?? 0
    if (lp && f.renderTime > lp.t) vy = (p.y - lp.y) / ((f.renderTime - lp.t) / 1000)
    else if (!lp) vy = p.y > 0.001 ? 1 : 0
    const dtMs = lp ? Math.max(0, f.renderTime - lp.t) : 0
    if (!lp || f.renderTime > lp.t) this.lastPos.set(p.id, { x: p.x, y: p.y, z: p.z, t: f.renderTime, vx, vz, vy })
    const velS = cam === 0 ? { x: vx, y: vz } : { x: -vx, y: -vz }
    const speed = Math.hypot(vx, vz)
    // Frozen pose for derez: use the last live pose.
    const tl = this.timelines.get(p.id)
    const frozen = tl && (tl.kind === 'undraw' || tl.kind === 'pendingDerez' || tl.kind === 'rescind')
    let st = p.st, stt = p.stt
    if (frozen) { const last = this.lastDerezPos.get(p.id); if (last) { st = last.st; stt = last.stt } }
    else if (p.alive) this.lastDerezPos.set(p.id, { x: p.x, z: p.z, ax: p.ax, az: p.az, st: p.st, stt: p.stt })
    // 11 §7 clips: resolved in the render-time domain (hitstop freezes them). The throw event was
    // stamped in wall time; translate its age so a skipped windup still plays from the event.
    const throwAt = this.throwStarts.get(p.id)
    const throwStartedMs = throwAt !== undefined && f.now - throwAt < 400 ? f.renderTime - (f.now - throwAt) : null
    const inp: ClipInput = { state: st, stateTicks: stt, speedFrac: Math.min(1.2, speed / MOVE_SPEED), gaitRad: gaitAngle(velS, aimS), alive: p.alive && !frozen, shieldAge: st === 'SHIELD' ? p.sag : null, throwStartedMs, nowMs: f.renderTime, grounded: p.g, y: p.y, landTicks: p.lt }
    const prevClips = this.clipStates.get(p.id)
    const clips = prevClips ? resolveClips(prevClips, inp, dtMs) : initialClips(f.renderTime, inp)
    this.clipStates.set(p.id, clips)
    // S10 jump clip (11 §7): airborne → rise/fall tuck by height (t01 = y / apex); landing lock → crouch.
    const jump: AthletePose['jump'] = !p.g
      ? { y: p.y, phase: vy >= 0 ? 'rise' : 'fall', t01: vy >= 0 ? Math.min(1, Math.max(0, p.y) / JUMP_APEX_M) : 1 - Math.min(1, Math.max(0, p.y) / JUMP_APEX_M) }
      : p.lt > 0 ? { y: 0, phase: 'land', t01: 1 - Math.min(1, p.lt / JUMP_LAND_TICKS) } : null
    return {
      state: st === 'DEREZ' ? 'IDLE' : st, stateTicks: stt, aimScreen: aimS, velScreen: velS, speedFrac: Math.min(1.2, speed / MOVE_SPEED),
      facing: p.team === cam ? 'back' : 'front', heightPx: PLAYER_HEIGHT * pr.scale, team: p.team,
      shield: st === 'SHIELD' ? { age: p.sag, perfect: p.sag <= SHIELD_PERFECT_TICKS } : null,
      held: p.held, hatch: p.team === 0 ? 'diag' : 'dots', glyph: glyphForPowerUp(p.pu), badges: p.pus.slice(1, 3).map((id) => glyphForPowerUp(id)), timeMs: frozen ? 0 : f.renderTime, local: p.local,
      lightDx: lightAzimuthSide(p.x, cam), clips, jump,
    }
  }

  /** Fog toward paper (spec §3): scale alpha for one entity draw. */
  private withFog(a: number, fn: () => void): void {
    if (a >= 0.999) { fn(); return }
    if (a <= 0.005) return
    this.ctx.save(); this.ctx.globalAlpha *= a; fn(); this.ctx.restore()
  }

  private drawPlayer(pr: Projected, p: RenderPlayer, f: WorldFrame, strokeOnly: boolean): void {
    const ctx = this.ctx
    const style = this.styleFor(p, f.now, f.options)
    if (style.layers.every((l) => l.alpha <= 1e-6) && style.heightTickAlpha <= 1e-6) return
    const cached = this.frameDrawings.get(p.id)
    const pose = cached?.pose ?? this.poseFor(p, pr, f)
    const drawing = cached?.drawing ?? buildAthlete(pose)
    const flick = this.flicker.get(p.id) === this.frameNo
    // 11 §8: held disc rings outline-only through COUNTDOWN and BETWEEN, accent pours in over 80 ms at GO;
    // at round end the held disc un-draws with the loose ones from discsUndrawAtMs.
    style.layers[10].fillFrac = Math.min(style.layers[10].fillFrac, this.beat.heldDiscFill, this.beat.betweenOutline ? 0 : 1)
    if (f.mode === 'match' && f.state.match.phase === 'ROUND_END' && this.phaseMs >= this.beat.discsUndrawAtMs) {
      const u = discUndrawStyle(this.phaseMs - this.beat.discsUndrawAtMs)
      style.layers[10].strokeProgress = Math.min(style.layers[10].strokeProgress, u.ringProgress)
      style.layers[10].fillFrac = Math.min(style.layers[10].fillFrac, u.fillAlpha)
    }
    drawAthlete(ctx, drawing, style, {
      sx: pr.sx, sy: pr.sy, team: p.team, fillsOff: strokeOnly || flick, patternStrength: f.options.patternStrength, highContrast: f.options.highContrast,
      glyph: pose.glyph, badges: pose.badges, jitterSeed: f.now, local: p.local, perfectBright: (f.now - (this.perfectBright.get(p.id) ?? -1e9)) < 40,
    })
    // 06 §5 far-distance callout for enemies (spectators: everyone).
    if (pose.glyph && pose.glyph !== 'empty' && (p.team !== f.cameraTeam || f.spectator) && style.layers[6].alpha > 0.5) {
      const px = pr.sx + (drawing.plate?.cx ?? 0), py = pr.sy - (drawing.plate?.cy ?? drawing.heightPx * 0.6)
      const tagX = px + 26, tagY = py - 34
      ctx.strokeStyle = `rgba(${INK_RGB},0.7)`; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + 12, py - 14); ctx.lineTo(tagX - 14, tagY + 13); ctx.stroke()
      // Open-L bracket on the leader corner (never a closed box): 18 px arms, 2 px (15 §3.1, 09 governs).
      ctx.lineWidth = 2
      ctx.beginPath(); ctx.moveTo(tagX - 14, tagY + 13 - 18); ctx.lineTo(tagX - 14, tagY + 13); ctx.lineTo(tagX - 14 + 18, tagY + 13); ctx.stroke()
      // The callout lists all three: match pick large, then the imprints (06 §5 / 12 §2).
      drawGlyph(ctx, pose.glyph, tagX, tagY, 22)
      let bx = tagX + 22
      for (const g of (pose.badges ?? []).filter((x) => x && x !== 'empty')) { drawGlyph(ctx, g, bx, tagY + 3, 14); bx += 18 }
    }
    if (f.debug) {
      const r = PLAYER_RADIUS * pr.scale
      ctx.strokeStyle = 'rgba(255,80,80,0.8)'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.ellipse(pr.sx, pr.sy, r, r * 0.35, 0, 0, Math.PI * 2); ctx.stroke()
      ctx.fillStyle = '#fff'; ctx.font = '10px monospace'; ctx.textAlign = 'left'
      const tl = this.timelines.get(p.id)
      ctx.fillText(`${p.st}${p.st === 'SHIELD' ? ' age ' + p.sag : ''}${tl ? ' ' + tl.kind : ''}`, pr.sx + r + 4, pr.sy - drawing.heightPx)
    }
  }

  private drawGhost(pr: Projected, p: RenderPlayer, f: WorldFrame, alpha: number): void {
    const pose = this.frameDrawings.get(p.id)?.pose ?? this.poseFor(p, pr, f)
    const drawing = buildAthlete({ ...pose, held: 0 })
    const style = normalStyle()
    for (const l of style.layers) { l.fillFrac = 0; l.alpha *= 0.5 * alpha }
    style.layers[5].alpha = 0; style.layers[9].alpha = 0; style.layers[0].alpha = 0
    drawAthlete(this.ctx, drawing, style, { sx: pr.sx, sy: pr.sy, team: p.team, fillsOff: true, patternStrength: 0, highContrast: false, glyph: null, jitterSeed: 0, local: false, perfectBright: false, noAccent: true })
  }

  // ── Discs ──────────────────────────────────────────────────────────────────
  private drawDisc(pr: Projected, d: RenderDisc, P: (x: number, y: number, z: number) => Projected, f: WorldFrame, strokeOnly: boolean): void {
    const ctx = this.ctx
    const accent = TEAM_ACCENT[d.team]
    const r = Math.max(3, DISC_RADIUS * pr.scale)
    const ry = r * 0.45
    let motif: DiscStyle | null = null
    const m = this.discMotifs.get(d.id)
    if (m) {
      const t = f.now - m.startAt
      motif = m.kind === 'redraw' ? discRedrawStyle(t) : discUndrawStyle(t)
      if (motif.done || (m.kind === 'undraw' && t >= FX_DISC_UNDRAW_MS) || (m.kind === 'redraw' && t >= FX_DISC_REDRAW_MS)) { this.discMotifs.delete(d.id); motif = null }
      else if (t < 0) motif = { tickAlpha: 0, ringProgress: 1, fillAlpha: 1, done: false }
    }
    ctx.save()
    const ringProgress = motif ? motif.ringProgress : 1
    const fillAlpha = (motif ? motif.fillAlpha : 1) * (strokeOnly ? 0 : 1)
    if (motif && motif.tickAlpha > 0) {
      // `Ø 0.44` dimension tick for the re-materialise motif.
      ctx.globalAlpha = motif.tickAlpha; ctx.strokeStyle = INK; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(pr.sx - r - 4, pr.sy - ry - 6); ctx.lineTo(pr.sx + r + 4, pr.sy - ry - 6); ctx.stroke()
      ctx.fillStyle = INK; ctx.font = '9px ui-monospace, monospace'; ctx.textAlign = 'left'; ctx.fillText('Ø 0.44', pr.sx + r + 6, pr.sy - ry - 3)
      ctx.globalAlpha = 1
    }
    if (f.options.highContrast) { ctx.strokeStyle = '#0b0d10'; ctx.lineWidth = 5; ctx.beginPath(); ctx.ellipse(pr.sx, pr.sy, r, ry, 0, 0, Math.PI * 2); ctx.stroke() }
    // 15 §2.3 face: two flat tones split at the floodlight azimuth, accent rim, team hatch spinning with
    // the disc (render time, never wall clock), state outline (09 §2 redundancy).
    const moving = d.st === 'FLIGHT' || d.st === 'RETURNING'
    const face = discFace({ r, spin: moving ? discSpin(f.renderTime) : 0, team: d.team, state: d.st, lightDx: lightAzimuthSide(d.x, f.cameraTeam) })
    ctx.translate(pr.sx, pr.sy)
    const path = (pl: Polyline) => { ctx.beginPath(); pl.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y))); ctx.closePath() }
    const rim = (prog: number) => { ctx.beginPath(); ctx.ellipse(0, 0, r, ry, 0, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * prog) }
    const paintFace = (alpha: number, ground: boolean) => {
      if (alpha <= 0) return
      ctx.globalAlpha = alpha
      if (ground) { ctx.fillStyle = `rgba(${INK_RGB},0.12)`; path(face.rim); ctx.fill() }
      else {
        ctx.fillStyle = '#cfd4d8'; path(face.rim); ctx.fill()
        if (face.unlit.length) { ctx.fillStyle = '#8f969c'; path(face.unlit); ctx.fill() }
        if (r >= 5 && f.options.patternStrength > 0) {
          ctx.save(); path(face.rim); ctx.clip()
          ctx.globalAlpha = alpha * 0.5 * f.options.patternStrength; ctx.strokeStyle = INK; ctx.lineWidth = 1; ctx.lineCap = 'round'
          ctx.beginPath(); for (const sg of face.hatch) { ctx.moveTo(sg[0].x, sg[0].y); ctx.lineTo(sg[1].x, sg[1].y) } ctx.stroke()
          ctx.restore()
        }
      }
      ctx.globalAlpha = 1
    }
    const outline = () => { ctx.setLineDash(face.outline === 'dashed' ? [6, 4] : face.outline === 'dotted' ? [2, 3] : []) }
    switch (d.st) {
      case 'HELD':
        if (d.outline || this.beat.betweenOutline || fillAlpha <= 0) { ctx.strokeStyle = accent; ctx.lineWidth = 1.5; rim(ringProgress); ctx.stroke() }
        else { paintFace(fillAlpha, false); ctx.strokeStyle = accent; ctx.lineWidth = 2; rim(ringProgress); ctx.stroke() }
        break
      case 'FLIGHT':
        paintFace(fillAlpha, false)
        ctx.strokeStyle = accent; ctx.lineWidth = 2.5; outline(); rim(ringProgress); ctx.stroke(); ctx.setLineDash([])
        if (r > 9) drawGlyph(ctx, d.team === 0 ? 'teamA' : 'teamB', 0, 0, r * 0.5, `rgba(${INK_RGB},0.7)`)
        break
      case 'RETURNING':
        ctx.globalAlpha = 0.7
        paintFace(fillAlpha * 0.55, false)
        ctx.globalAlpha = 0.7; ctx.strokeStyle = accent; ctx.lineWidth = 2; outline(); rim(ringProgress); ctx.stroke(); ctx.setLineDash([])
        if (r > 9) drawGlyph(ctx, d.team === 0 ? 'teamA' : 'teamB', 0, 0, r * 0.5, `rgba(${INK_RGB},0.4)`)
        break
      case 'GROUND': {
        paintFace(fillAlpha, true)
        ctx.strokeStyle = accent; ctx.lineWidth = 1.5; outline(); rim(ringProgress); ctx.stroke(); ctx.setLineDash([])
        // Timeout ring (09 §2): radius 0.45 m, un-draws clockwise over DISC_GROUND_TIMEOUT_TICKS.
        if (d.stt !== undefined) {
          const left = 1 - Math.min(1, d.stt / DISC_GROUND_TIMEOUT_TICKS)
          ctx.strokeStyle = `rgba(${INK_RGB},0.5)`; ctx.lineWidth = 1
          ctx.beginPath(); ctx.ellipse(0, 0, 0.45 * pr.scale, 0.45 * pr.scale * 0.35, 0, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * left); ctx.stroke()
        }
        // Pickup radius when the local player is within 2 m.
        if (f.localPos && Math.hypot(f.localPos.x - d.x, f.localPos.z - d.z) < 2) { ctx.setLineDash([1, 3]); ctx.strokeStyle = `rgba(${INK_RGB},0.35)`; ctx.beginPath(); ctx.ellipse(0, 0, 0.7 * pr.scale, 0.7 * pr.scale * 0.35, 0, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]) }
        break
      }
      default: break
    }
    ctx.translate(-pr.sx, -pr.sy)
    if (f.debug && (d.st === 'FLIGHT' || d.st === 'RETURNING')) {
      const e = P(d.x + d.vx * TICK_DT, DISC_FLIGHT_Y, d.z + d.vz * TICK_DT)
      ctx.strokeStyle = '#ff0'; ctx.lineWidth = 1; ctx.setLineDash([]); ctx.globalAlpha = 1
      ctx.beginPath(); ctx.moveTo(pr.sx, pr.sy); ctx.lineTo(e.sx, e.sy); ctx.stroke()
    }
    ctx.restore()
  }

  private drawTrail(P: (x: number, y: number, z: number) => Projected, tr: { kind: 'solid' | 'dashed'; team: Team; state: DiscStateName; samples: { x: number; z: number }[] }, o: RenderOptions): void {
    const ctx = this.ctx
    if (tr.samples.length < 2) return
    const pts = tr.samples.map((s) => P(s.x, DISC_FLIGHT_Y, s.z)).filter((p) => p.visible)
    if (pts.length < 2) return
    const style = this.trails.styleFor(tr.state)
    ctx.save()
    ctx.lineCap = 'round'
    if (style.dashed) {
      // RETURNING: dashed 6/4 px neutral ink, no core (11 §4).
      ctx.setLineDash([6, 4]); ctx.strokeStyle = `rgba(${INK_RGB},0.6)`; ctx.lineWidth = 1
      ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p.sx, p.sy) : ctx.moveTo(p.sx, p.sy))); ctx.stroke()
    } else {
      // FLIGHT: ink stroke tapering 3 → 0 px toward the tail (static width under reduced motion).
      const n = pts.length
      for (let i = 1; i < n; i++) {
        const f = i / (n - 1)
        ctx.strokeStyle = `rgba(${INK_RGB},${0.6 * f})`; ctx.lineWidth = o.reducedMotion ? 2 : 3 * f
        ctx.beginPath(); ctx.moveTo(pts[i - 1].sx, pts[i - 1].sy); ctx.lineTo(pts[i].sx, pts[i].sy); ctx.stroke()
      }
      if (style.core) {
        ctx.strokeStyle = TEAM_ACCENT[tr.team]; ctx.lineWidth = 1; ctx.globalAlpha = 0.9
        ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p.sx, p.sy) : ctx.moveTo(p.sx, p.sy))); ctx.stroke()
      }
    }
    ctx.restore()
  }

  /** 11 §4 GROUND: faint skid stroke behind the landing point, fading over 200 ms. */
  private drawSkid(P: (x: number, y: number, z: number) => Projected, fl: Flourish, now: number): void {
    const ctx = this.ctx
    const t = (now - fl.bornAt) / FLOURISH_MS.skid
    if (t > 1) return
    const dx = fl.dx ?? 0, dz = fl.dz ?? 1
    const a = P(fl.x - dx * 0.6, 0, fl.z - dz * 0.6), b = P(fl.x, 0, fl.z)
    if (!a.visible || !b.visible) return
    ctx.save(); ctx.strokeStyle = `rgba(${INK_RGB},${0.45 * (1 - t)})`; ctx.lineWidth = 2; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke(); ctx.restore()
  }

  private drawSpark(P: (x: number, y: number, z: number) => Projected, s: { kind: string; x: number; z: number; dx: number; dz: number; len: number; bornAt: number; lifeMs: number; collapse: boolean }, now: number): void {
    const ctx = this.ctx
    const age = (now - s.bornAt) / s.lifeMs
    const a = Math.max(0, 1 - age)
    // Sparks fly outward (or collapse inward for the perfect ring).
    const d = s.collapse ? s.len * (1 - age) : s.len * (0.2 + 0.8 * age)
    const x0 = s.x + s.dx * (s.collapse ? d : d * 0.5), z0 = s.z + s.dz * (s.collapse ? d : d * 0.5)
    const x1 = s.x + s.dx * (s.collapse ? d + 0.12 : d), z1 = s.z + s.dz * (s.collapse ? d + 0.12 : d)
    const p0 = P(x0, DISC_FLIGHT_Y, z0), p1 = P(x1, DISC_FLIGHT_Y, z1)
    if (!p0.visible || !p1.visible) return
    ctx.strokeStyle = `rgba(${INK_RGB},${a})`; ctx.lineWidth = s.kind === 'whoosh' ? 2 : 1
    ctx.beginPath(); ctx.moveTo(p0.sx, p0.sy); ctx.lineTo(p1.sx, p1.sy); ctx.stroke()
  }

  private drawFlourish(pr: Projected, fl: Flourish, now: number): void {
    const ctx = this.ctx
    const t = (now - fl.bornAt) / (fl.kind === 'steal' ? 120 : 80)
    if (t > 1) return
    const r = 0.5 * pr.scale
    ctx.save(); ctx.strokeStyle = `rgba(${INK_RGB},${1 - t})`; ctx.lineWidth = 1.5
    if (fl.kind !== 'pickup') {
      // Closing bracket: two L corners closing in on the hand.
      const g = r * (1.6 - 0.6 * t), a = r * 0.35
      ctx.beginPath(); ctx.moveTo(pr.sx - g, pr.sy - g + a); ctx.lineTo(pr.sx - g, pr.sy - g); ctx.lineTo(pr.sx - g + a, pr.sy - g)
      ctx.moveTo(pr.sx + g, pr.sy + g - a); ctx.lineTo(pr.sx + g, pr.sy + g); ctx.lineTo(pr.sx + g - a, pr.sy + g); ctx.stroke()
    }
    if (fl.kind === 'steal') { ctx.globalAlpha = 0.5 * (1 - t); ctx.fillStyle = TEAM_ACCENT[fl.team]; ctx.beginPath(); ctx.arc(pr.sx, pr.sy, r * 0.4 * t, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
  }

  private drawDecal(P: (x: number, y: number, z: number) => Projected, d: { x: number; z: number; nx: number; nz: number; value: string }, alpha: number): void {
    const ctx = this.ctx
    const p = P(d.x, DISC_FLIGHT_Y, d.z); if (!p.visible) return
    const s = 10 // virtual px at projected scale (fixed-size, 11 §4)
    // Tangent in screen space ≈ perpendicular to the projected normal.
    const n = P(d.x + d.nx * 0.5, DISC_FLIGHT_Y, d.z + d.nz * 0.5)
    let tx = -(n.sy - p.sy), ty = n.sx - p.sx; const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl
    let nx = n.sx - p.sx, ny = n.sy - p.sy; const nl = Math.hypot(nx, ny) || 1; nx /= nl; ny /= nl
    ctx.strokeStyle = `rgba(${INK_RGB},${0.7 * alpha})`; ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(p.sx - tx * s * 0.5, p.sy - ty * s * 0.5); ctx.lineTo(p.sx + tx * s * 0.5, p.sy + ty * s * 0.5)
    ctx.moveTo(p.sx, p.sy); ctx.lineTo(p.sx + nx * s, p.sy + ny * s); ctx.stroke()
    ctx.fillStyle = `rgba(${INK_RGB},${0.6 * alpha})`; ctx.font = '8px ui-monospace, monospace'; ctx.textAlign = 'left'
    ctx.fillText(d.value, p.sx + nx * s + 2, p.sy + ny * s + 3)
  }

  // ── Shield arcs (11 §5 / 09 §2) ────────────────────────────────────────────
  private drawShieldArc(pr: Projected, p: RenderPlayer, now: number, f: WorldFrame): void {
    const ctx = this.ctx
    const accent = TEAM_ACCENT[p.team]
    const cam = f.cameraTeam
    const center = cam === 0 ? Math.atan2(-p.az, p.ax) : Math.atan2(p.az, -p.ax)
    const rad = SHIELD_RADIUS * pr.scale
    const lowered = this.lowering.get(p.id)
    const isShield = p.st === 'SHIELD' && p.alive
    let progress = 1, erase = false
    if (!isShield) {
      if (lowered === undefined || now - lowered > 34 || p.st === 'EXHAUST') { this.lowering.delete(p.id); return } // exhaust: instant vanish
      progress = 1 - (now - lowered) / 34; erase = true
    } else progress = Math.min(1, (p.sag + 1) / 2) // pen draws the arc over 2 frames
    const perfect = isShield && p.sag <= SHIELD_PERFECT_TICKS
    const shudder = (this.shudder.get(p.id) ?? -1) > now ? Math.sin(((now - ((this.shudder.get(p.id) ?? 0) - 90)) / 90) * Math.PI * 6) * 2 : 0
    ctx.save()
    ctx.translate(pr.sx, pr.sy - 1.0 * pr.scale)
    ctx.scale(1, 0.5)
    ctx.strokeStyle = accent
    ctx.lineWidth = perfect ? 4 : 2
    ctx.globalAlpha = perfect ? 1 : 0.8
    if (p.sta < 25 && isShield) ctx.setLineDash([6, 4])
    const a0 = erase ? center - ARC_HALF + ARC_HALF * 2 * (1 - progress) : center - ARC_HALF
    const a1 = center - ARC_HALF + ARC_HALF * 2 * (erase ? 1 : progress)
    ctx.beginPath(); ctx.arc(0, 0, rad + shudder, a0, a1); ctx.stroke()
    if (perfect) { ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(0, 0, rad + 0.05 * pr.scale + 4 + shudder, a0, a1); ctx.stroke() }
    if (p.local && isShield) { ctx.lineWidth = 1.5; for (const a of [a0, a1]) { ctx.beginPath(); ctx.moveTo(Math.cos(a) * (rad - 4), Math.sin(a) * (rad - 4)); ctx.lineTo(Math.cos(a) * (rad + 4), Math.sin(a) * (rad + 4)); ctx.stroke() } }
    ctx.restore()
    // Hold callout after 0.5 s (the world annotates the turtle).
    const since = this.shieldHoldSince.get(p.id)
    if (isShield && since !== undefined && now - since > 500) {
      ctx.fillStyle = `rgba(${INK_RGB},0.55)`; ctx.font = '10px ui-monospace, monospace'; ctx.textAlign = 'center'
      ctx.fillText(`${SHIELD_ARC_HALF_DEG * 2}°`, pr.sx + Math.cos(center) * rad * 1.1, pr.sy - 1.0 * pr.scale + Math.sin(center) * rad * 0.55 - 6)
    }
    if (isShield && since === undefined) this.shieldHoldSince.set(p.id, now - p.sag * TICK_DT * 1000)
  }

  // ── Screen-edge indicators (11 §6) ─────────────────────────────────────────
  private drawEdgeIndicators(P: (x: number, y: number, z: number) => Projected, f: WorldFrame, local: RenderPlayer | null): void {
    const ctx = this.ctx
    const cam = f.cameraTeam
    const me = local ?? null
    const cands: { d: RenderDisc; eta: number; pr: Projected }[] = []
    let threat: { team: Team; ang: number } | null = null
    for (const d of f.discs) {
      if (d.st !== 'FLIGHT' || d.team === cam) continue
      const pr = P(d.x, d.y, d.z)
      const off = !pr.visible || pr.sx < 0 || pr.sx > VIRTUAL_W || pr.sy < 0 || pr.sy > VIRTUAL_H
      const speed = Math.hypot(d.vx, d.vz) || 1
      let eta = Infinity
      if (me) {
        const rx = me.x - d.x, rz = me.z - d.z
        const along = (rx * d.vx + rz * d.vz) / speed
        const dist = Math.hypot(rx, rz)
        eta = along > 0 ? dist / speed : Infinity
        // Closest approach for the threat tint + near-miss.
        const perp = Math.abs(rx * d.vz - rz * d.vx) / speed
        if (along > 0 && eta < FX_THREAT_TIME_S && perp < 1.5) threat = { team: d.team, ang: Math.atan2(-(cam === 0 ? rz : -rz), cam === 0 ? -rx : rx) }
        if (dist < PLAYER_RADIUS + DISC_RADIUS + 0.5 && !this.nearMissSeen.has(d.id)) { this.nearMissSeen.add(d.id); this.sparks.burst('whoosh', `nm${d.id}:${Math.round(f.renderTime)}`, d.x, d.z, d.vx, d.vz, f.now) }
      }
      if (off) cands.push({ d, eta, pr })
    }
    cands.sort((a, b) => a.eta - b.eta)
    const t = f.now / 1000
    const pulse = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(t * 6 * Math.PI * 2))
    for (const c of cands.slice(0, FX_CHEVRON_MAX)) {
      // Direction from screen centre to the (clamped) projection; behind-camera → bottom edge at its x.
      let tx: number, ty: number
      if (!c.pr.visible) { tx = Math.max(24, Math.min(VIRTUAL_W - 24, c.pr.sx)); ty = VIRTUAL_H - 24 }
      else {
        const dx = c.pr.sx - VIRTUAL_W / 2, dy = c.pr.sy - VIRTUAL_H / 2
        const k = Math.min((VIRTUAL_W / 2 - 24) / Math.max(1e-6, Math.abs(dx)), (VIRTUAL_H / 2 - 24) / Math.max(1e-6, Math.abs(dy)))
        tx = VIRTUAL_W / 2 + dx * k; ty = VIRTUAL_H / 2 + dy * k
      }
      const ang = Math.atan2(ty - VIRTUAL_H / 2, tx - VIRTUAL_W / 2)
      ctx.save(); ctx.translate(tx, ty); ctx.rotate(ang)
      ctx.globalAlpha = pulse * (f.spectator ? 0.6 : 1)
      ctx.strokeStyle = TEAM_ACCENT[c.d.team]; ctx.lineWidth = 2.5
      ctx.beginPath(); ctx.moveTo(-9, -9); ctx.lineTo(0, 0); ctx.lineTo(-9, 9); ctx.stroke()
      ctx.restore()
      drawGlyph(ctx, c.d.team === 0 ? 'teamA' : 'teamB', tx - Math.cos(ang) * 22, ty - Math.sin(ang) * 22, 12)
    }
    // Threat edge tint: 24 px inward accent gradient at the edge nearest the bearing.
    if (threat && !f.options.flashReduction && me) {
      const tt = f.now
      if (this.threatAt < 0 || tt - this.threatAt > 200) this.threatAt = tt
      this.threatLast = tt
      const c = Math.cos(threat.ang), s = Math.sin(threat.ang)
      const horiz = Math.abs(c) > Math.abs(s)
      const grad = horiz
        ? ctx.createLinearGradient(c > 0 ? VIRTUAL_W : 0, 0, c > 0 ? VIRTUAL_W - 24 : 24, 0)
        : ctx.createLinearGradient(0, s > 0 ? VIRTUAL_H : 0, 0, s > 0 ? VIRTUAL_H - 24 : 24)
      grad.addColorStop(0, hexA(TEAM_ACCENT[threat.team], 0.12)); grad.addColorStop(1, hexA(TEAM_ACCENT[threat.team], 0))
      ctx.fillStyle = grad
      if (horiz) ctx.fillRect(c > 0 ? VIRTUAL_W - 24 : 0, 0, 24, VIRTUAL_H); else ctx.fillRect(0, s > 0 ? VIRTUAL_H - 24 : 0, VIRTUAL_W, 24)
    }
    if (!threat && this.threatLast > 0 && f.now - this.threatLast < 120 && !f.options.flashReduction) {
      // 120 ms release.
      const a = 0.12 * (1 - (f.now - this.threatLast) / 120)
      ctx.fillStyle = `rgba(${INK_RGB},${a * 0.3})`; ctx.fillRect(0, VIRTUAL_H - 24, VIRTUAL_W, 24)
    }
  }
  private threatAt = -1
  private threatLast = -1

  // ── Static helpers ─────────────────────────────────────────────────────────
  private drawOtRegion(P: (x: number, y: number, z: number) => Projected, b: Bounds, full: Bounds, grid: CanvasImageSource): void {
    const ctx = this.ctx
    // Clip = full zone quad minus the live bounds quad (even-odd), then paint the grid-only bake through it.
    const quad = (q: Bounds) => [P(q.xMin, 0, q.zMin), P(q.xMax, 0, q.zMin), P(q.xMax, 0, q.zMax), P(q.xMin, 0, q.zMax)]
    const outer = quad(full), inner = quad(b)
    if (outer.some((p) => !p.visible)) return
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(outer[0].sx, outer[0].sy); for (const p of outer.slice(1)) ctx.lineTo(p.sx, p.sy); ctx.closePath()
    ctx.moveTo(inner[0].sx, inner[0].sy); for (const p of inner.slice(1)) ctx.lineTo(p.sx, p.sy); ctx.closePath()
    ctx.clip('evenodd')
    ctx.drawImage(grid, 0, 0, VIRTUAL_W, VIRTUAL_H)
    ctx.restore()
  }

  private drawBounds(P: (x: number, y: number, z: number) => Projected, b: Bounds, color: string, callout: boolean, tm: Team): void {
    const ctx = this.ctx
    const c = [P(b.xMin, 0, b.zMin), P(b.xMax, 0, b.zMin), P(b.xMax, 0, b.zMax), P(b.xMin, 0, b.zMax)]
    ctx.save(); ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.setLineDash([8, 4])
    ctx.beginPath(); ctx.moveTo(c[0].sx, c[0].sy); for (const p of c.slice(1)) ctx.lineTo(p.sx, p.sy); ctx.closePath(); ctx.stroke()
    if (callout) {
      // Live dimension line: `d = x.x m` at the moving back bound (blueprint register, neutral ink).
      const back = tm === 0 ? b.zMin : b.zMax
      const full = tm === 0 ? 0 : 30
      const m = P((b.xMin + b.xMax) / 2, 0, back)
      if (m.visible) { ctx.setLineDash([]); ctx.fillStyle = `rgba(${INK_RGB},0.85)`; ctx.font = '11px ui-monospace, monospace'; ctx.textAlign = 'center'; ctx.fillText(`d = ${Math.abs(back - full).toFixed(1)} m`, m.sx, m.sy - 6) }
    }
    ctx.restore()
  }

  private drawGizmosStatic(P: (x: number, y: number, z: number) => Projected, arena: Arena): void {
    const ctx = this.ctx
    ctx.fillStyle = 'rgba(120,200,255,0.7)'
    for (const tm of [0, 1] as Team[]) for (const s of [...arena.spawns[tm], ...arena.hubSpawns[tm]]) { const p = P(s.x, 0, s.z); if (p.visible) ctx.fillRect(p.sx - 2, p.sy - 2, 4, 4) }
    ctx.fillStyle = 'rgba(255,255,255,0.8)'; ctx.font = '10px monospace'; ctx.textAlign = 'left'
    for (const z of [3, 27]) { const p = P(0, 0, z); ctx.fillText(`z=${z} y=${p.sy.toFixed(0)} s=${p.scale.toFixed(0)}`, p.sx + 6, p.sy) }
    // Wall/pillar normals.
    ctx.strokeStyle = 'rgba(255,120,120,0.6)'; ctx.lineWidth = 1
    for (const w of arena.walls) { const mx = (w.ax + w.bx) / 2, mz = (w.az + w.bz) / 2; const dx = w.bx - w.ax, dz = w.bz - w.az; const l = Math.hypot(dx, dz) || 1; const a = P(mx, 0.5, mz), b = P(mx + (-dz / l) * 0.8, 0.5, mz + (dx / l) * 0.8); if (a.visible && b.visible) { ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke() } }
  }
}

// ── Stroke-list painter ───────────────────────────────────────────────────────
export interface DrawAthleteOpts {
  sx: number; sy: number; team: Team; fillsOff: boolean; patternStrength: number; highContrast: boolean
  glyph: GlyphName | null; badges?: GlyphName[]; jitterSeed: number; local: boolean; perfectBright: boolean; noAccent?: boolean
}

function polyLength(pl: Polyline): number { let l = 0; for (let i = 1; i < pl.length; i++) l += Math.hypot(pl[i].x - pl[i - 1].x, pl[i].y - pl[i - 1].y); return l }

/** Render an AthleteDrawing under a LayerStyle. Layers draw in index order;
 *  strokes erase end→start via a dash trick; fills clip bottom→top. */
export function drawAthlete(ctx: CanvasRenderingContext2D, d: AthleteDrawing, style: LayerStyle, o: DrawAthleteOpts): void {
  ctx.save()
  ctx.translate(o.sx, o.sy)
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'
  const H = d.heightPx
  const lw = Math.max(1, Math.min(2.2, H / 90)) * (o.highContrast ? 2 : 1)
  const jit = style.jitterPx
  const jx = jit ? Math.sin(o.jitterSeed * 0.37) * jit : 0, jy = jit ? Math.cos(o.jitterSeed * 0.53) * jit : 0
  const accent = TEAM_ACCENT[o.team]
  const fillColour = (kind: string): string => {
    switch (kind) { case 'head': return '#c9b3a0'; case 'arms': return '#cfc9c2'; case 'torso': return '#b8bcc0'; case 'legs': return '#5c636b'; case 'feet': return '#3d434a'; case 'plate': return '#2a3037'; case 'disc': return '#cfd4d8'; default: return '#9aa1a8' }
  }
  const path = (pl: Polyline) => { ctx.beginPath(); pl.forEach((q, i) => (i ? ctx.lineTo(q.x + jx, -q.y + jy) : ctx.moveTo(q.x + jx, -q.y + jy))) }
  for (const L of d.layers) {
    const ls = style.layers[L.index]
    const alpha = L.index === 0 ? ls.alpha : ls.alpha
    if (alpha <= 1e-6) continue
    // Fills (clipped bottom→top by fillFrac).
    if (!o.fillsOff && ls.fillFrac > 1e-6 && L.fills.length && !(o.noAccent && L.accent)) {
      ctx.save(); ctx.globalAlpha = alpha
      if (ls.fillFrac < 1) { ctx.beginPath(); ctx.rect(-H, -H * ls.fillFrac * 1.02, 2 * H, H * ls.fillFrac * 1.02 + 4); ctx.clip() }
      ctx.fillStyle = L.accent ? accent : fillColour(L.kind)
      for (const f of L.fills) { path(f); ctx.closePath(); ctx.fill() }
      // 15 §1.4 one hard floodlight step: the unlit half at tone × 0.72 (a flat shade, no gradient).
      if (L.fills2.length) { ctx.fillStyle = 'rgba(0,0,0,0.28)'; for (const f of L.fills2) { path(f); ctx.closePath(); ctx.fill() } }
      ctx.restore()
    }
    // Hatch: clipped to the jersey polygon, opacity × patternStrength.
    if (L.kind === 'hatch') {
      if (o.patternStrength <= 0 || ls.alpha <= 0) continue
      ctx.save(); path(d.jersey); ctx.closePath(); ctx.clip()
      ctx.globalAlpha = Math.min(1, alpha * 0.6 * o.patternStrength); ctx.strokeStyle = INK; ctx.lineWidth = o.highContrast ? 1.5 : 1
      ctx.beginPath(); for (const s of L.strokes) { ctx.moveTo(s[0].x + jx, -s[0].y + jy); for (const q of s.slice(1)) ctx.lineTo(q.x + jx, -q.y + jy) } ctx.stroke()
      ctx.restore()
      continue
    }
    // Strokes (erased end→start by strokeProgress).
    if (ls.strokeProgress > 1e-6 && L.strokes.length) {
      ctx.save()
      ctx.globalAlpha = alpha
      const isTick = L.index === 0, isCons = L.index === 1
      ctx.strokeStyle = L.kind === 'disc' && !o.noAccent ? accent : isTick || isCons ? `rgba(${INK_RGB},0.9)` : INK
      ctx.lineWidth = isTick || isCons ? 1 : L.kind === 'disc' ? lw * 1.2 : lw
      if (isCons) ctx.setLineDash([3, 3])
      for (const s of L.strokes) {
        if (ls.strokeProgress < 1) { const len = polyLength(s); ctx.setLineDash([Math.max(0.01, len * ls.strokeProgress), 1e6]); ctx.lineDashOffset = 0 }
        path(s); ctx.stroke()
      }
      // Height tick label + other labels.
      if (isTick && L.labels.length) {
        ctx.setLineDash([]); ctx.font = `${Math.max(7, Math.min(11, H / 16))}px ui-monospace, monospace`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
        L.labels.forEach((lab, i) => { ctx.globalAlpha = i === 0 ? style.heightTickAlpha : alpha; ctx.fillStyle = `rgba(${INK_RGB},0.9)`; if (ctx.globalAlpha > 1e-3) ctx.fillText(lab.text, lab.x + jx, -lab.y + jy) })
      }
      ctx.restore()
    }
    // Height tick may outlive the rest of layer 0 (S5 hold).
    if (L.index === 0 && style.heightTickAlpha > alpha + 1e-6) {
      ctx.save(); ctx.globalAlpha = style.heightTickAlpha; ctx.strokeStyle = `rgba(${INK_RGB},0.9)`; ctx.lineWidth = 1
      for (const s of L.strokes.slice(0, 3)) { path(s); ctx.stroke() }
      const lab = L.labels[0]; if (lab) { ctx.font = `${Math.max(7, Math.min(11, H / 16))}px ui-monospace, monospace`; ctx.fillStyle = `rgba(${INK_RGB},0.9)`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(lab.text, lab.x, -lab.y) }
      ctx.restore()
    }
    // Chest plate glyph (neutral ink, never accent) drawn with the plate layer.
    if (L.kind === 'plate' && d.plate && o.glyph && o.glyph !== 'empty' && ls.strokeProgress > 0.5 && !o.fillsOff) {
      const size = Math.min(d.plate.w, d.plate.h) * 0.8
      if (size >= 6) { ctx.save(); ctx.globalAlpha = alpha; drawGlyph(ctx, o.glyph, d.plate.cx, -d.plate.cy, size); ctx.restore() }
    }
    // 12 §2 imprints as two shoulder badges (neutral ink, small; public like the match pick).
    if (L.kind === 'plate' && d.plate && o.badges?.length && ls.strokeProgress > 0.5 && !o.fillsOff) {
      const bsize = Math.max(0, Math.min(d.plate.w, d.plate.h) * 0.42)
      if (bsize >= 5) for (let i = 0; i < Math.min(2, o.badges.length); i++) { const g = o.badges[i]; if (g && g !== 'empty' && d.badges[i]) { ctx.save(); ctx.globalAlpha = alpha; drawGlyph(ctx, g, d.badges[i].x, -d.badges[i].y, bsize); ctx.restore() } }
    }
    // Team glyph etched in the plate corner (09 §3 redundancy).
    if (L.kind === 'plate' && d.plate && ls.strokeProgress > 0.5 && d.plate.w > 10) {
      ctx.save(); ctx.globalAlpha = alpha * 0.8; drawGlyph(ctx, o.team === 0 ? 'teamA' : 'teamB', d.plate.cx + d.plate.w * 0.38, -d.plate.cy + d.plate.h * 0.38, Math.max(4, d.plate.w * 0.28)); ctx.restore()
    }
  }
  if (o.highContrast) {
    // 2 px dark outline around the silhouette (torso + head), under the ink.
    ctx.save(); ctx.globalCompositeOperation = 'destination-over'; ctx.strokeStyle = '#0b0d10'; ctx.lineWidth = lw + 4
    for (const L of d.layers) if (L.fills.length && style.layers[L.index].alpha > 0) for (const f of L.fills) { path(f); ctx.closePath(); ctx.stroke() }
    ctx.restore()
  }
  if (o.local) {
    // Faint local marker: a short centre tick under the feet (blueprint register, no box).
    ctx.strokeStyle = `rgba(${INK_RGB},0.5)`; ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(-H * 0.18, 4); ctx.lineTo(H * 0.18, 4); ctx.stroke()
  }
  ctx.restore()
  void FX_TICK_ALPHA_IDLE; void AMBER
}

function hexA(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}
/** For a multiply flash at alpha α: multiply by lerp(white, accent, α). */
function mixToWhite(hex: string, keepWhite: number): string {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  const m = (c: number) => Math.round(255 * keepWhite + c * (1 - keepWhite))
  return `rgb(${m(r)},${m(g)},${m(b)})`
}
